import { Box, Button, Grid, Stack, Typography } from '@mui/material';
import { VyIcon } from '@weft/icons';
import type { MeterInput, NewMeter } from '@weft/types';
import { CRITERION_OPERATORS } from '@weft/types';
import { isEmptyArray, isNotEmptyArray } from '@weft/utils';
import React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useEffectOnce } from '../../../../../hooks/use-effect-once';
import { SectionContainer } from '../SectionContainer';
import { SectionContainerItem } from '../SectionContainerItem';
import { CriterionsItems } from './create-product-page-conditions-components/CriterionsItems';

export const CreateProductPageConditions: React.FC = () => {
  const { control, setValue } = useFormContext<MeterInput, unknown, NewMeter>();

  const conditions = useFieldArray({
    control,
    name: 'filter.conditions',
  });

  useEffectOnce(() => {
    if (conditions.fields.length === 0) {
      setValue('filter', undefined);
    }
  });

  const handleAddCondition = () => {
    conditions.append({
      criterions: [
        {
          field: '',
          operator: CRITERION_OPERATORS.Contains,
          value: '',
        },
      ],
    });
  };

  const handleRemoveCondition = (index: number) => {
    const conditionsLength = conditions.fields.length;
    conditions.remove(index);

    if (conditionsLength === 1) {
      setValue('filter', undefined);
    }
  };

  return (
    <SectionContainer
      title="Conditions (optional)"
      cta={isEmptyArray(conditions.fields) && (
        <Button
          variant="contained"
          color="secondary"
          size="medium"
          sx={{
            columnGap: 1.5,
          }}
          onClick={handleAddCondition}
        >
          <VyIcon icon="PlusMedium"/>
          Add Condition
        </Button>
      )}
    >
      {isNotEmptyArray(conditions.fields) && (
        <Stack
          rowGap={2}
          sx={{
            position: 'relative',
            '::before': {
              content: '""',
              position: 'absolute',
              left: 'calc(50% - 0.5px)',
              display: 'block',
              width: '1px',
              backgroundColor: 'rgba(0, 0, 0, 0.12)',
              height: '100%',
              zIndex: 0,
            },
            '> *': {
              position: 'relative',
              zIndex: 1,
            },
          }}
        >
          {conditions.fields.map((condition, conditionIndex) => (
            <React.Fragment key={condition.id}>
              {conditionIndex > 0 && (
                <Box sx={{
                  margin: 'auto',
                  border: '1px solid rgba(0, 0, 0, 0.12)',
                  background: '#fff',
                  padding: '6px 16px',
                  borderRadius: '4px',
                }}
                >
                  <Typography color="secondary">Or</Typography>
                </Box>
              )}

              <SectionContainerItem key={condition.id}>
                <Grid
                  container
                  rowSpacing={2}
                >
                  <CriterionsItems
                    conditionIndex={conditionIndex}
                    onLastCriterionDelete={() => handleRemoveCondition(conditionIndex)}
                  />
                </Grid>
              </SectionContainerItem>
            </React.Fragment>
          ))}

          {isNotEmptyArray(conditions.fields) && (
            <Box sx={{
              margin:'auto',
            }}
            >
              <Button
                variant="contained"
                color="secondary"
                size="medium"
                sx={theme => ({
                  columnGap: '8px',
                  paddingX: '20px',
                  backgroundColor: '#fff',
                  border: `1px solid ${theme.palette.divider}`,
                })}
                onClick={handleAddCondition}
              >
                <VyIcon
                  icon="PlusMedium"
                  style={{
                    transform: 'scale(0.8)',
                  }}
                />
                Or
              </Button>
            </Box>
          )}
        </Stack>
      )}
    </SectionContainer>
  );
};